import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Keyboard, Platform, TouchableOpacity, View } from 'react-native';

import { isFunction as _isFunction } from 'lodash';
import PropTypes from 'prop-types';

import { ContentManager } from '@vfgroup-oneplatform/foundation';
import { Icon } from '@vfgroup-oneplatform/foundation/Components';
import {
  Colors,
  useTheme,
  getThemeImages as FoundationGetThemeImages,
} from '@vfgroup-oneplatform/foundation/Components/Themes';
import { TextField } from '@vfgroup-oneplatform/foundation/Components/VFInput/MaterialTextField';

import { getThemeImages } from '../../../Themes/Modes';
import SohoFrameworkTestKeys from '../../../Utils/SohoFrameworkTestKeys';
import useKeyboardInfo from '../../Hooks/KeyboardAvoiding/KeyboardAvoiding.Hooks';

import styles from './SearchBar.Styles';

const SearchBar = React.forwardRef(
  (
    {
      filterKeyword,
      placeholder,
      data,
      style,
      onFilterData,
      onStartTyping,
      onActive: _onActive,
      onChangeText: _onChangeText,
      onShowKeyboard,
    },
    ref,
  ) => {
    const theme = useTheme();
    const sohoImages = getThemeImages(theme);
    const FoundationImages = FoundationGetThemeImages(theme);
    const isFirstTimeTyping = useRef(true);
    const textFieldRef = useRef();

    const { keyboardHeight } = useKeyboardInfo();

    const [isTextFieldActive, setIsTextFieldActive] = useState(false);

    useImperativeHandle(ref, () => ({
      resetSearchBarText: () => {
        textFieldRef.current.setValue('');
      },
      blurSearchBar: () => {
        textFieldRef.current.blur();
      },
    }));

    useEffect(() => {
      isTextFieldActive && _isFunction(_onActive) && _onActive();
    }, [isTextFieldActive]);

    useEffect(() => {
      if (isTextFieldActive && keyboardHeight !== 0 && _isFunction(onShowKeyboard)) {
        onShowKeyboard(keyboardHeight);
      }
    }, [keyboardHeight, isTextFieldActive]);

    useEffect(() => {
      let backBtnEventHandler;
      if (Platform.OS === 'android') {
        backBtnEventHandler = Keyboard.addListener('keyboardDidHide', () => {
          textFieldRef.current.blur();
        });
      }
      return () => {
        backBtnEventHandler.remove();
      };
    }, []);

    const filterData = text => {
      let filteredData = [];

      if (text === '') {
        filteredData = data;
      } else {
        filteredData = data.map(dataArray =>
          dataArray.filter(obj => obj[filterKeyword].toLowerCase().includes(text.toLowerCase())),
        );
      }

      onFilterData(text, filteredData);
    };

    const onChangeText = text => {
      if (isFirstTimeTyping.current && _isFunction(onStartTyping)) {
        isFirstTimeTyping.current = false;
        onStartTyping();
      }
      textFieldRef.current.setValue(text);
      filterData(text);
      _isFunction(_onChangeText) && _onChangeText();
    };

    const renderXBtn = () => {
      return (
        isTextFieldActive && (
          <TouchableOpacity
            testID={SohoFrameworkTestKeys.SearchBarClearBtn}
            onPress={() => onChangeText('')}
            style={styles.xButtonStyle}
          >
            <Icon name={FoundationImages.ic_close} style={styles.xIconStyle} size={21} />
          </TouchableOpacity>
        )
      );
    };

    const renderSearchIcon = () => {
      return (
        <Icon
          name={sohoImages.ic_search}
          testID={SohoFrameworkTestKeys.SearchBarSearchBtn}
          style={styles.iconStyle}
          size={18}
        />
      );
    };

    return (
      <View style={[styles.searchStyle, style]}>
        <TextField
          renderLeftAccessory={renderSearchIcon}
          ref={textFieldRef}
          onFocus={() => setIsTextFieldActive(!isTextFieldActive)}
          onBlur={() => setIsTextFieldActive(!isTextFieldActive)}
          onChangeText={onChangeText}
          renderRightAccessory={renderXBtn}
          animationDuration={0}
          activeLineWidth={2}
          lineWidth={1}
          underlineColorAndroid={'transparent'}
          lineType={'none'}
          placeholder={
            placeholder
              ? ContentManager.translate(placeholder)
              : ContentManager.translate('soho_search_bar_placeholder')
          }
          inputContainerStyle={styles.textFieldInputContainerStyle(isTextFieldActive)}
          containerStyle={styles.textInputStyle}
          baseColor={styles.textFieldColor.baseColor}
          tintColor={styles.textFieldColor.tintColor}
          placeholderTextColor={Colors.smoothGrey}
        />
      </View>
    );
  },
);

SearchBar.propTypes = {
  filterKeyword: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
  onFilterData: PropTypes.func.isRequired,
  onStartTyping: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  style: PropTypes.object,
  onActive: PropTypes.func,
  onChangeText: PropTypes.func,
  onShowKeyboard: PropTypes.func,
};

export default SearchBar;
